import { useState, useEffect, useCallback, useRef } from "react";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import { getSharedSelection, setSharedSelection } from "./sharedSelection";
import { getNavFrom } from "./navSource";
import "./FilterBar.css";
import { getVerticalStacks, getSubStacks, getJobTitles } from "./api";

// Small caps icon: chevron down for dropdown affordance
function ChevronDown() {
  return (
    <svg width="12" height="12" viewBox="0 0 12 12" fill="none" className="fb-chevron">
      <path d="M2.5 4.5L6 8L9.5 4.5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

// Matches the real search-pill icon used elsewhere in the Minerva shell
function SearchIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" width="15" height="15">
      <circle cx="11" cy="11" r="7" />
      <line x1="21" y1="21" x2="16.65" y2="16.65" />
    </svg>
  );
}

// Exact SVGs from Minerva_intelligence_UI_prototype.html #view-comp export buttons
function PdfIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
      <path d="M14 2v6h6" />
    </svg>
  );
}

function XlsIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
      <rect x="3" y="3" width="18" height="18" rx="2" />
      <path d="M3 9h18M9 3v18" />
    </svg>
  );
}

// Ported from old Angular's core-compensation.component.ts getQuarterTimePeriod().
// Auto-selects "this" quarter (e.g. "2026 Q1") by matching today's date
// against the loaded time-periods list by name, so the Role table has a
// time_period_id to query with the instant a Vertical is picked - the user
// never has to touch the Quarter dropdown themselves. Falls back to the
// latest quarter before today's if the current one isn't in the list yet
// (same fallback the old Angular used).
export function getDefaultQuarter(periods) {
  if (!periods || periods.length === 0) return null;

  const now = new Date();
  const year = now.getUTCFullYear();
  const month = now.getUTCMonth() + 1;
  const quarter = month <= 3 ? 1 : month <= 6 ? 2 : month <= 9 ? 3 : 4;
  const currentName = `${year} Q${quarter}`;

  const exact = periods.find((p) => p.name === currentName);
  if (exact) return exact;

  const parseName = (name) => {
    const m = name?.match(/^(\d{4}) Q(\d)$/);
    return m ? { year: parseInt(m[1], 10), quarter: parseInt(m[2], 10) } : null;
  };

  const past = periods
    .filter((p) => {
      const parsed = parseName(p.name);
      return parsed && (parsed.year < year || (parsed.year === year && parsed.quarter < quarter));
    })
    .sort((a, b) => {
      const pa = parseName(a.name);
      const pb = parseName(b.name);
      if (pa.year !== pb.year) return pb.year - pa.year;
      return pb.quarter - pa.quarter;
    });

  return past[0] || null;
}

// Ported from old Angular's stack-model.ts isCore(): these 7 sub-stack ids
// are the "core" ones. Selecting one of these collapses the Sub Stack
// dropdown into a "Clear Core Stack" button (clearCoreStack() in
// dropdown-section.component.ts), instead of staying a normal dropdown.
const CORE_SUB_STACK_IDS = [92, 100, 101, 103, 117, 126, 129];

// Synthetic option prepended to every Sub Stack list, matching old Angular's
// dropdown-section.component.ts (`{ id: 0, name: 'All Roles', ... }` unshift
// in both ngOnChanges() and loadData()). Selecting it means "don't filter by
// sub stack" - App.js's role-list effect treats id === 0 as no sub-stack filter.
const ALL_ROLES_OPTION = { id: 0, name: "All Roles" };

// Synthetic option prepended to the Vertical Stack list, matching old
// Angular's dropdown-section.component.ts loadData(): `[{ id: 0, name:
// 'All', value: 'All', isActive: true }, ...verticalStacks]` (only skipped
// when isBlendedRole - not the case for either comp-page dropdown-section
// usage). Selecting it (vertical_stack_id=0) already works end-to-end
// server-side without any backend change: compensation_pivot.sql's own
// `WHERE (c.vertical_stack_id = :vertical_stack_id OR :vertical_stack_id = 0)`
// already treats 0 as "every vertical".
const ALL_VERTICAL_OPTION = { id: 0, name: "All" };

// Ported from core-compensation.component.ts's sortOrder (same constant used
// identically in RangeBarsChart.jsx/DistributionChart.jsx/CostBandTable.jsx/
// ComparisonChart.jsx) - fixed display order by territory_id (UK, Ireland,
// South Africa, Romania, India, Brazil). exportCompensationXls() below never
// applied this sort, so its rows came back in whatever order the API
// returned them (alphabetical) - confirmed still wrong via screenshot even
// after the earlier duplication fix.
const TERRITORY_SORT_ORDER = [4, 5, 2, 3, 1, 6];

/**
 * FilterBar
 *
 * Row 1: Core Vertical Stack -> Sub Stack -> Job Title (cascading) + search + export
 * Row 2: Quarter, Territory (independent, no cascade)
 *
 * Cascade rules (matching old Minerva / real schema):
 *  - Sub Stacks are fetched by VerticalStackId, with a synthetic "All Roles"
 *    option prepended (id: 0)
 *  - Job Titles are fetched by SubStackId (GET /api/JobTitles/GetBySubStackId/{id}),
 *    except when Sub Stack is "All Roles" (id 0), where every job title for
 *    the selected Vertical is loaded instead (ported from old Angular's
 *    onSubStackChange(): model.subStackId === 0 -> filter allJobTitles by
 *    verticalStackId rather than calling the sub-stack-scoped endpoint)
 *  - Selecting a field ahead of the chain resets everything downstream of it
 *  - Picking one of the 7 "core" sub stacks (CORE_SUB_STACK_IDS) collapses
 *    the Sub Stack dropdown into a "Clear Core Stack" button
 *  - The search box matches job title name / job code / alternate titles
 *    across ALL job titles, and jumping to a match resolves and sets the
 *    full Vertical -> Sub Stack -> Job Title chain (ported from old
 *    Angular's searchbar.component.ts filterJobTitles()/updateJobTitleIds())
 *  - Clicking a role directly in RoleListTable (App.js's clickedRole,
 *    passed down as `syncRole`) resolves and sets the same chain - ported
 *    from old Angular's core-compensation.component.ts highlightRole(),
 *    which pushes the clicked row's verticalStackId/subStackId/jobTitleId
 *    into the app-wide Config.stackModel that dropdown-section.component.ts
 *    reads. Without this, picking a "core" role from the table never shows
 *    the Clear Core Stack button, since that button is driven entirely by
 *    this component's own Sub Stack selection.
 *
 * activeJobTitleId / activeRoleName (from App.js): which role is currently
 * on screen (via table click or the Job Title dropdown) - needed by the
 * Export XLS button, which is scoped to one role same as old Minerva.
 *
 * row2Content (from App.js): the Quarter + Territory bar. FIXED 2026-08-05
 * (3rd attempt on this bug) - checked old Angular's actual component tree
 * again: `app-dropdown-section` (this file's Row 1 - Vertical/Sub Stack/Job
 * Title/search/export) lives ONLY in the persistent parent
 * (recruitment-component.component.html), never duplicated. Quarter +
 * Territory are NOT part of it at all - they're each their own state,
 * independently duplicated inside core-compensation.component.ts
 * (role-list page, `selectedTerritories`/max 2) and
 * non-core-compensation.component.ts (job-detail page,
 * `model.territoryIds`/unlimited) - i.e. inside whichever page component is
 * currently mounted, destroyed with it, fresh on the other. Two earlier
 * attempts kept Quarter+Territory living IN this persistent FilterBar
 * (first as one shared control, then as a shared control with a manual
 * reset effect on the mode-crossing) - both keep this component simpler,
 * but neither is what old code actually does, and the manual-reset version
 * still left a one-render-cycle gap where App.js's cost-band-detail-driving
 * components could mount/fetch with stale pre-crossing values (a real race,
 * confirmed against old code as impossible there since old never has more
 * than one fetch in flight for this data to begin with).
 *
 * Fix: App.js now renders RoleListTerritoryQuarterRow.jsx or
 * JobDetailTerritoryQuarterRow.jsx (mutually exclusive, based on
 * activeJobTitleId) and passes the result in here as row2Content - React
 * unmounts/remounts between them exactly like old's component swap, so
 * their own useState initial values ARE the reset, not a delayed
 * correction. See those two files for the rest of this fix (including the
 * single cost-band-detail fetch that replaces three redundant per-chart
 * fetches).
 */
// A role pick waiting to be finished after App remounts this bar for a new
// ?stack= - see step -1 of FilterBar's initial-load effect.
let resumeRole = null;

export default function FilterBar({ onSelectionChange, activeJobTitleId, activeRoleName, syncRole, onClearClickedRole, row2Content, exportRows, urlStackId, urlSubStackId, onVerticalChange, onSubStackChange }) {
  const [verticalStacks, setVerticalStacks] = useState([]);
  const [subStacks, setSubStacks] = useState([]);
  // Seeded from ?sub= and consumed by the sub-stack cascade below.
  const pendingSubStackRef = useRef(urlSubStackId ?? null);
  const [jobTitles, setJobTitles] = useState([]);

  // Boots with "All" pre-selected (per 18-Aug call: no landing page - the
  // comp page itself opens populated with the all-verticals role table).
  const [selectedVertical, setSelectedVertical] = useState(ALL_VERTICAL_OPTION);
  const [selectedSubStack, setSelectedSubStack] = useState(null);
  const [selectedJobTitle, setSelectedJobTitle] = useState(null);

  // All job titles, fetched once on mount - powers the search box independent
  // of whatever the cascade dropdowns currently have selected (matches old
  // Angular's this.allJobTitles, loaded once in dropdown-section's loadData()).
  const [searchIndex, setSearchIndex] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [searchOpen, setSearchOpen] = useState(false);
  const searchWrapRef = useRef(null);

  const isCoreSubStack = selectedSubStack && CORE_SUB_STACK_IDS.includes(selectedSubStack.id);

  // --- Initial loads: vertical stacks, search index are independent of the cascade ---
  useEffect(() => {
    // Read the shared selection SYNCHRONOUSLY, before any await: App.js's
    // own write-effect clears sharedSelection when it mounts with no active
    // role, and parent effects run right after this child effect's body -
    // reading inside the .then below would always see the cleared value
    // (verified live: the restore silently never fired until this capture).
    const shared = getSharedSelection();
    // Same synchronous capture for a parked role pick (see step -1): under
    // StrictMode both mounts must see it, not just the first.
    const resume = resumeRole;

    Promise.all([
      getVerticalStacks().catch(() => []),
      getJobTitles().catch(() => []),
    ]).then(([rows, titles]) => {
      const verticals = [ALL_VERTICAL_OPTION, ...(rows || [])];
      setVerticalStacks(verticals);
      setSearchIndex(titles || []);

      // -1) FINISH A ROLE PICK THAT CHANGED THE VERTICAL (2026-09-25): App
      //     re-keys this bar on ?stack=, so a search pick or JD·Skills
      //     restore in another vertical remounts it mid-selection. The pick
      //     is parked in `resumeRole` (module scope, survives the remount)
      //     and completed here, now that the URL already names its vertical.
      if (resume) {
        resumeRole = null;
        selectSearchResultRef.current?.(resume, verticals);
        return;
      }

      // 0) BACK FROM JD·SKILLS WITH A ROLE (FIXED 2026-09-25): the role
      //    picked there wins over ?stack=, because the URL still holds
      //    whatever vertical Comp had BEFORE the trip - JD·Skills doesn't
      //    write its picks into the URL. Only when the rail trip really came
      //    from JD·Skills: a jd tag left over from an earlier visit must not
      //    override a vertical chosen since (e.g. on Compare).
      //    The fresh `verticals` list is passed in: the ref still holds the
      //    previous render's selectSearchResult, whose verticalStacks is the
      //    empty initial state - it resolved the vertical to null, wiped
      //    ?stack= and left Core Vertical blank (her staging report, 25 Sep).
      const jdTitle = shared?.source === "jd" && shared.jobTitleId != null
        ? (titles || []).find((t) => t.id === shared.jobTitleId)
        : null;
      if (jdTitle && getNavFrom() === "/jd-skills") {
        // Consume the tag so the next Comp visit doesn't re-apply it.
        setSharedSelection({ ...shared, source: undefined });
        selectSearchResultRef.current?.(jdTitle, verticals);
        return;
      }

      // 1) THE URL IS THE SOURCE OF TRUTH for the vertical (2026-09-18):
      //    /comp?stack=4 opens on Finance. That covers the gateway
      //    rectangles, the rail carrying the stack between pages, a
      //    refresh (F5), and a pasted link - one mechanism instead of the
      //    in-memory hand-off this replaced. Applied inside this load,
      //    before anything renders with All, so there's no All -> Finance
      //    flash (the flash that killed the old restore-on-mount effect
      //    below). Wins over the jd tag: it is the more explicit request.
      if (urlStackId != null) {
        const match = (rows || []).find((v) => v.id === urlStackId);
        if (match) {
          setSelectedVertical(match);
          return;
        }
      }

      // 2) TWO-WAY SYNC (ADDED 2026-09-18): restore a role picked on the
      //    JD Spec Comparison tab - ONLY selections tagged source:'jd'
      //    (JobSpecComparison.jsx writes the tag). Untagged selections are
      //    Comp's own writes and are deliberately NOT restored, keeping the
      //    20-Aug removal's rationale intact (no re-restoring a role the
      //    user cleared here, no restore on every ordinary return).
      //    Resolves the full chain through the same path a search pick
      //    uses, so the page lands exactly as a manual walk would.
      if (jdTitle) {
        setSharedSelection({ ...shared, source: undefined });
        selectSearchResultRef.current?.(jdTitle, verticals);
      }
    });
    // Mount-only: the URL vertical is read once here; re-running on every
    // change would fight the user's own dropdown edits.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const selectSearchResultRef = useRef(null);

  // REMOVED 2026-08-20: a restore-last-role-on-mount effect used to live
  // here, so returning from JD·Skills put Comp back on the role you had
  // open. Removed by decision after testing it: the carry-over is ONE-WAY
  // only (Comp -> JD·Skills). Coming back to Comp always lands on the All
  // default. The restore made the page visibly flash All -> Finance Manager
  // on every return (two renders, second one after its fetches resolved),
  // and interacted badly with Clear Core Stack, which cleared the sub stack
  // but left the role to be re-restored on the next visit.

  // --- Sub Stacks depend on selected Vertical Stack (server-side filter), with "All Roles" prepended ---
  useEffect(() => {
    if (!selectedVertical) {
      setSubStacks([]);
      return;
    }
    // FIXED 2026-08-20: an earlier round claimed old shows an EMPTY Sub Stack
    // dropdown for the "All" vertical - that read the wrong branch. Old's
    // onVerticalStackChange() null/All path does `this.subStacks =
    // this.allSubStacks` (dropdown-section.component.ts:178-180) - i.e. All
    // shows EVERY sub stack across verticals, with "All Roles" prepended by
    // clearCoreStack()'s recruitment-page unshift. Ported to match: All ->
    // fetch with no vertical filter (backend returns all).
    getSubStacks(selectedVertical.id === 0 ? undefined : selectedVertical.id)
      .then((rows) => {
        setSubStacks([ALL_ROLES_OPTION, ...rows]);
        // Apply a sub stack carried in the URL (?sub=), once - matching what
        // old Minerva does on its own pages: leaving Comp and coming back
        // keeps the vertical AND the sub stack, but NOT the job title (her
        // observation on staging, 18 Sep). Consumed via the ref so a later
        // manual change here isn't undone on the next cascade run.
        if (pendingSubStackRef.current != null) {
          const match = rows.find((r) => r.id === pendingSubStackRef.current);
          pendingSubStackRef.current = null;
          if (match) setSelectedSubStack(match);
        }
      })
      .catch(() => setSubStacks([ALL_ROLES_OPTION]));
  }, [selectedVertical]);

  // --- Job Titles depend on selected Sub Stack (server-side filter), except
  //     "All Roles" (id 0), which loads every job title for the Vertical instead ---
  // Job Title dropdown ordering (ADDED 2026-08-20): seniority first - old
  // sorts every job-title list jobLevel DESC in onSubStackChange()
  // (dropdown-section.component.ts:204), which is why old's dropdown leads
  // with CHRO etc. while ours led with id-order (Executive Assistant, id 1).
  // Secondary id ASC makes ties deterministic - old's own tie order is just
  // its API's OrderBy(Id), so this matches it too.
  const byLevelThenId = (a, b) => ((b.job_level ?? 0) - (a.job_level ?? 0)) || (a.id - b.id);

  useEffect(() => {
    if (!selectedSubStack) {
      setJobTitles([]);
      return;
    }
    if (selectedSubStack.id === 0) {
      // "All Roles": scope to the selected Vertical - unless the Vertical is
      // itself "All" (id 0), where old's null-path shows every job title
      // (jobTitles = allJobTitles), so no filter at all (FIXED 2026-08-20:
      // the unconditional filter matched nothing for vertical id 0, leaving
      // the Job Title dropdown empty under All > All Roles).
      getJobTitles()
        .then((all) => setJobTitles(
          (selectedVertical?.id === 0 ? all : all.filter((jt) => jt.vertical_stack_id === selectedVertical?.id))
            .slice().sort(byLevelThenId)
        ))
        .catch(() => setJobTitles([]));
      return;
    }
    getJobTitles(selectedSubStack.id)
      .then((rows) => setJobTitles((rows || []).slice().sort(byLevelThenId)))
      .catch(() => setJobTitles([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSubStack, selectedVertical]);

  // --- Sync from a RoleListTable row click (App.js's clickedRole, passed as
  //     syncRole) - ported from old Angular's highlightRole(), see the
  //     component docstring above. Only reacts to genuinely new roles (by
  //     job_title_id), so it doesn't fight the user's own dropdown picks or
  //     re-fire on every render. subStacks is expected to already hold the
  //     clicked role's sub_stack_id, since the table is only shown for the
  //     currently-selected Vertical and subStacks is fetched for that same
  //     Vertical - falls back to a minimal {id, name} option if not found
  //     yet (e.g. subStacks still loading) so the Clear Core Stack check
  //     still works off the id.
  //
  // FIXED 2026-08-05: added a guard rejecting a syncRole whose
  // vertical_stack_id (present on every GetCompensationPivot row) doesn't
  // match the currently selected Vertical. Old never needs this - it has no
  // separate "remembered clicked role" to go stale at all (see App.js's
  // clearClickedRole note). This is defense-in-depth for our split-state
  // port: onClearClickedRole (below) is the primary fix, synchronously
  // clearing App.js's clickedRole from every FilterBar change handler so
  // this effect never sees a stale role in the first place - this guard
  // catches any other path that might leave clickedRole stale regardless.
  useEffect(() => {
    if (!syncRole || syncRole.job_title_id == null) return;
    if (syncRole.vertical_stack_id != null && selectedVertical && syncRole.vertical_stack_id !== selectedVertical.id) return;
    if (selectedJobTitle?.id === syncRole.job_title_id) return;

    if (syncRole.sub_stack_id != null) {
      const sub = subStacks.find((s) => s.id === syncRole.sub_stack_id) || { id: syncRole.sub_stack_id, name: "" };
      setSelectedSubStack(sub);
    } else {
      setSelectedSubStack(null);
    }

    setSelectedJobTitle({
      id: syncRole.job_title_id,
      name: syncRole.role,
      job_code: syncRole.job_code,
      source_job_title_alternative: syncRole.source_job_title_alternative,
    });
  }, [syncRole, subStacks, selectedJobTitle, selectedVertical]);

  // --- Notify parent whenever the resolved selection changes ---
  // FIXED 2026-08-05: no longer includes quarter/territory - those are now
  // owned by whichever of RoleListTerritoryQuarterRow.jsx /
  // JobDetailTerritoryQuarterRow.jsx is currently mounted (see row2Content
  // note above), reported to App.js directly by those components instead of
  // through here.
  useEffect(() => {
    onSelectionChange?.({
      verticalStack: selectedVertical,
      subStack: selectedSubStack,
      jobTitle: selectedJobTitle,
    });
  }, [selectedVertical, selectedSubStack, selectedJobTitle, onSelectionChange]);

  // --- Close the search results dropdown on outside click ---
  useEffect(() => {
    function handleClickOutside(e) {
      if (searchWrapRef.current && !searchWrapRef.current.contains(e.target)) {
        setSearchOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // FIXED 2026-08-05: each of these now also clears App.js's clickedRole
  // synchronously (onClearClickedRole) - see App.js's clearClickedRole note
  // and the syncRole effect's guard above for the full explanation. Without
  // this, changing Vertical/Sub Stack/Job Title while a table-clicked role
  // is still active gets undone a render later by the syncRole effect
  // reasserting the stale role.
  const handleVerticalChange = useCallback((option) => {
    onClearClickedRole?.();
    // Write the vertical into the URL (?stack=) so it survives a refresh
    // and travels with the rail. Done here, in the user's action, rather
    // than in an effect on selectedVertical: under StrictMode the effect
    // re-runs on the remount while this bar is still on its All default
    // and published All over the real choice (seen live: /comp?stack=4
    // became /comp?stack=0 immediately after the gateway hand-off).
    // Also drops ?sub= - the cascade resets Sub Stack (one URL write; see
    // App.js setUrlStack for why not two).
    onVerticalChange?.(option?.id ?? null);
    setSelectedVertical(option);
    setSelectedSubStack(null);
    setSelectedJobTitle(null);
  }, [onClearClickedRole, onVerticalChange]);

  const handleSubStackChange = useCallback((option) => {
    onClearClickedRole?.();
    onSubStackChange?.(option?.id ?? null);
    setSelectedSubStack(option);
    setSelectedJobTitle(null);
  }, [onClearClickedRole, onSubStackChange]);

  const handleJobTitleChange = useCallback((option) => {
    onClearClickedRole?.();
    setSelectedJobTitle(option);
  }, [onClearClickedRole]);

  // Ported from clearCoreStack() in dropdown-section.component.ts - drops
  // back to a normal Sub Stack dropdown (and clears the downstream Job Title),
  // without touching the selected Vertical.
  // CORRECTED 2026-08-05: an earlier round claimed this also resets
  // Territory ("confirmed by testing... against the real old app") - that
  // was wrong. Re-checked core-compensation.component.ts directly this
  // round: `verticalStackId`/`subStackId` are @Input()s on a component that
  // stays MOUNTED across Vertical/Sub Stack changes while still in
  // role-list mode (recruitment-component.component.html wraps it in one
  // `@if(!hasStacks)`, which Clear Core Stack never flips) - so Clear Core
  // Stack only re-runs loadData() on the SAME instance via
  // Config.stackModelChanged, and loadData() never touches
  // this.selectedTerritories anywhere in its body. Territory only resets
  // when the whole component is destroyed/recreated - i.e. only on the
  // role-list <-> job-detail boundary (see RoleListTerritoryQuarterRow.jsx),
  // never on Clear Core Stack. Removed the incorrect reset.
  const handleClearCoreStack = useCallback(() => {
    onClearClickedRole?.();
    setSelectedSubStack(null);
    setSelectedJobTitle(null);
  }, [onClearClickedRole]);

  // Ported from searchbar.component.ts's filterJobTitles(): matches name,
  // job code, or ';'-delimited alternate titles, case-insensitively.
  const handleSearchChange = (value) => {
    setSearchTerm(value);
    setSearchOpen(value.trim().length > 0);
  };

  const searchResults = (() => {
    const q = searchTerm.trim().toLowerCase();
    if (!q) return [];
    return searchIndex
      .filter((jt) => {
        if (jt.name?.toLowerCase().includes(q)) return true;
        if (jt.job_code?.toLowerCase().includes(q)) return true;
        const alts = (jt.source_job_title_alternative || "").toLowerCase().split(";");
        return alts.some((alt) => alt.trim().includes(q));
      })
      .slice(0, 20);
  })();

  // Ported from searchbar.component.ts's updateJobTitleIds(): resolves and
  // sets the full Vertical -> Sub Stack -> Job Title chain from a picked
  // search match, so the rest of the page (Role table / Cost Band Detail /
  // charts) ends up in exactly the state a manual dropdown walk would leave it in.
  // `verticalList` lets the mount-time JD restore pass the list it just
  // loaded - see step 0 in the initial-load effect.
  const selectSearchResult = async (match, verticalList = verticalStacks) => {
    onClearClickedRole?.();
    setSearchTerm("");
    setSearchOpen(false);

    const vertical = verticalList.find((v) => v.id === match.vertical_stack_id) || null;
    onVerticalChange?.(vertical?.id ?? null, match.sub_stack_id ?? null);
    // A different vertical changes ?stack=, which remounts this bar (App
    // keys it on the URL vertical) - park the pick for the new instance to
    // finish (step -1 of the initial-load effect).
    if ((vertical?.id || null) !== (urlStackId || null)) {
      resumeRole = match;
      return;
    }
    setSelectedVertical(vertical);
    setSelectedJobTitle(null);

    if (match.sub_stack_id != null) {
      try {
        const subs = await getSubStacks(match.vertical_stack_id);
        const sub = subs.find((s) => s.id === match.sub_stack_id) || null;
        setSubStacks([ALL_ROLES_OPTION, ...subs]);
        setSelectedSubStack(sub);
      } catch {
        setSelectedSubStack(null);
      }
    } else {
      setSelectedSubStack(null);
    }

    setSelectedJobTitle(match);
  };
  // Expose to the mount effect above (two-way JD -> Comp restore).
  selectSearchResultRef.current = selectSearchResult;

  return (
    <div className="fb-wrap">
      <div className="fb-row fb-row-cascade">
        <Dropdown
          label="Core Vertical"
          value={selectedVertical}
          placeholder="Select vertical stack"
          options={verticalStacks}
          getLabel={(o) => o.name}
          onChange={handleVerticalChange}
        />

        <span className="fb-sep">»</span>

        {/* FIXED 2026-08-05: was showing "Sub Stack: Core Stack [Clear Core
            Stack x]" as a single pill, plus a separate, still-labeled "Job
            Title" field after it - a 4th element that doesn't exist in old
            Angular. dropdown-section.component.html only ever shows 3
            elements: Vertical, then EITHER the Sub Stack dropdown OR a
            plain "Clear Core Stack" button (no value/label on it) in that
            same slot, then the Job Title field - which is the exact same
            field, just relabeled to "Core Stack" via
            `[title]="model.isCore() ? 'Core Stack: ' : 'Job Title: '"`. */}
        {isCoreSubStack ? (
          <button type="button" className="fb-core-clear" onClick={handleClearCoreStack} title="Clear core stack selection">
            Clear Core Stack
          </button>
        ) : (
          <Dropdown
            label="Sub Stack"
            value={selectedSubStack}
            placeholder="Select any option"
            options={subStacks}
            getLabel={(o) => o.name}
            onChange={handleSubStackChange}
            disabled={!selectedVertical}
            clearable
            onClear={() => handleSubStackChange(null)}
          />
        )}

        <span className="fb-sep">»</span>

        <Dropdown
          label={isCoreSubStack ? "Core Stack" : "Job Title"}
          value={selectedJobTitle}
          placeholder="Select any option"
          options={jobTitles}
          getLabel={(o) => o.name}
          onChange={handleJobTitleChange}
          disabled={!selectedSubStack}
        />

        <div className="fb-spacer" />

        <div className="fb-search" ref={searchWrapRef}>
          <SearchIcon />
          <input
            type="text"
            placeholder="Search job title or job code"
            value={searchTerm}
            onChange={(e) => handleSearchChange(e.target.value)}
            onFocus={() => setSearchOpen(searchTerm.trim().length > 0)}
          />

          {searchOpen && (
            <div className="fb-search-results">
              {searchResults.length === 0 && <div className="fb-search-empty">No matches</div>}
              {searchResults.map((jt) => (
                <button
                  key={jt.id}
                  type="button"
                  className="fb-search-result"
                  onClick={() => selectSearchResult(jt)}
                >
                  <span className="fb-search-result-name">{jt.name}</span>
                  {jt.job_code && <span className="fb-search-result-code">{jt.job_code}</span>}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* FIXED 2026-08-05: was always rendered, relying on an alert() at
            click time to catch the no-job-title case. Old Angular hides the
            button entirely until a job title is selected
            (dropdown-section.component.html: `@if(model.jobTitleId !== null
            && showExportBtn)`) - matched here instead. */}
        {activeJobTitleId && (
          <ExportControl
            jobTitleId={activeJobTitleId}
            roleName={activeRoleName}
            rows={exportRows}
          />
        )}
      </div>

      <div className="fb-divider" />

      {row2Content}
    </div>
  );
}

// clearable/onClear: ported from PrimeNG's native showClear, which old
// Angular's shared <app-dropdown> only ever enables on the Sub Stack field
// (dropdown-section.component.ts's showClear=true, passed only to that one
// <app-dropdown>) - lets the user drop back to "only Vertical selected,
// role table showing" without reopening the menu and picking a value.
export function Dropdown({ label, value, placeholder, options, getLabel, onChange, disabled, clearable, onClear }) {
  const [open, setOpen] = useState(false);

  return (
    <div className={`fb-field ${disabled ? "fb-field-disabled" : ""}`}>
      <button
        type="button"
        className={`fb-field-btn${clearable ? " fb-field-btn-clearable" : ""}`}
        onClick={() => !disabled && setOpen((o) => !o)}
        disabled={disabled}
      >
        <span className="fb-field-label">{label}</span>
        <span className="fb-field-value">{value ? getLabel(value) : placeholder}</span>
        {clearable && value && (
          <span
            className="fb-field-clear"
            role="button"
            tabIndex={0}
            title="Clear selection"
            onClick={(e) => {
              e.stopPropagation();
              setOpen(false);
              onClear?.();
            }}
          >
            ×
          </span>
        )}
        <ChevronDown />
      </button>

      {open && !disabled && (
        <div className="fb-menu" onMouseLeave={() => setOpen(false)}>
          {options.length === 0 && <div className="fb-menu-empty">No options</div>}
          {options.map((opt) => (
            <button
              key={opt.id}
              type="button"
              className="fb-menu-item"
              onClick={() => {
                onChange(opt);
                setOpen(false);
              }}
            >
              {getLabel(opt)}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// Ported from PrimeNG's <p-multiselect> (non-core-compensation.component.html
// line 19: `[options]="territories" [(ngModel)]="model.territoryIds" ...
// display="chip"`) - a checkbox list that stays open across multiple picks,
// closing only on an outside click (same UX contract as the search results
// dropdown above, via its own ref/mousedown-outside effect). An empty
// `values` array is "nothing filtered" (all territories), matching old
// Angular's model.territoryIds default of [].
// maxSelection: when set, caps how many can be checked at once (ported from
// core-compensation.component.ts's `[selectionLimit]="maxTerritorySelection"`)
// - unchecked options past the cap show disabled, matching old's real
// checkbox behavior, rather than silently ignoring the click.
export function MultiSelectDropdown({ label, values, options, getLabel, onChange, placeholder, maxSelection }) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);

  useEffect(() => {
    function handleClickOutside(e) {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const toggleValue = (id) => {
    if (values.includes(id)) {
      onChange(values.filter((v) => v !== id));
      return;
    }
    if (maxSelection && values.length >= maxSelection) return; // hard cap
    onChange([...values, id]);
  };

  const selectedOpts = options.filter((o) => values.includes(o.id));
  const summary =
    selectedOpts.length === 0
      ? placeholder
      : selectedOpts.length <= 2
      ? selectedOpts.map(getLabel).join(", ")
      : `${selectedOpts.length} selected`;

  return (
    <div className="fb-field" ref={wrapRef}>
      <button type="button" className="fb-field-btn" onClick={() => setOpen((o) => !o)}>
        <span className="fb-field-label">{label}</span>
        <span className="fb-field-value">{summary}</span>
        <ChevronDown />
      </button>

      {open && (
        <div className="fb-menu fb-menu-check">
          {options.length === 0 && <div className="fb-menu-empty">No options</div>}
          {options.map((opt) => {
            const checked = values.includes(opt.id);
            const disabled = !checked && maxSelection && values.length >= maxSelection;
            return (
              <label key={opt.id} className={`fb-menu-check-item${disabled ? " fb-menu-check-item-disabled" : ""}`}>
                <input type="checkbox" checked={checked} disabled={disabled} onChange={() => toggleValue(opt.id)} />
                <span>{getLabel(opt)}</span>
              </label>
            );
          })}
        </div>
      )}
    </div>
  );
}

function applyBorder(cell) {
  cell.border = {
    top: { style: "thin" },
    left: { style: "thin" },
    bottom: { style: "thin" },
    right: { style: "thin" },
  };
}

// Ported from dropdown-section.component.ts's exportCompensationData():
// loads the pre-built data/CompensationData.xlsx template (same file as
// the old Angular app - see /public/data/CompensationData.xlsx), fills one
// row per territory for the currently selected role, and merges the
// Role/Job Code columns down the block.
//
// FIXED 2026-08-05: old's exportCompensationData() never fetches anything
// itself - it just reads `this.rawModel`, which is exportDataArray in the
// persistent parent, populated by whichever page component (core-
// compensation / non-core-compensation) last emitted its OWN already-
// fetched territoryData via `(exportData)="exportData($event)"`. This port
// used to do a brand-new, separately-parameterized fetch of its own on
// every click - a second, redundant fetch of the same data, same class of
// bug as the one fixed in CostBandTable.jsx/RangeBarsChart.jsx/
// DistributionChart.jsx this round. Now takes `rows` (App.js's
// costBandData, the one fetch JobDetailTerritoryQuarterRow.jsx makes) as a
// plain argument, matching old exactly - no fetch here at all.
async function exportCompensationXls(jobTitleId, roleName, rows) {
  // Old Angular hides the export button entirely until a job title is
  // selected (dropdown-section.component.html: `@if(model.jobTitleId !==
  // null && showExportBtn)`) - ExportControl below now matches that, so
  // this should be unreachable via the UI. Kept as a silent no-op safety
  // net rather than a user-facing alert (the alert was the actual bug the
  // always-visible button surfaced; the fix is not showing the button, not
  // the message on click).
  if (!jobTitleId || !rows || !rows.length) {
    return;
  }

  // Same TERRITORY_SORT_ORDER fix as before - rows may already be sorted by
  // JobDetailTerritoryQuarterRow.jsx, but re-sorting here is cheap and keeps
  // this function correct regardless of the caller.
  const sortedRows = [...rows].sort(
    (a, b) => TERRITORY_SORT_ORDER.indexOf(a.territory_id) - TERRITORY_SORT_ORDER.indexOf(b.territory_id)
  );

  const wb = new ExcelJS.Workbook();
  try {
    const buffer = await fetch("/data/CompensationData.xlsx").then((res) => res.arrayBuffer());
    await wb.xlsx.load(buffer);
  } catch (err) {
    console.error("Error loading Excel template:", err);
    return;
  }
  const ws = wb.getWorksheet(1);
  if (!ws) {
    console.error("Worksheet not found");
    return;
  }

  const finalRoleName = roleName ?? sortedRows[0].role ?? "";
  const startRow = 2;
  let currentRow = startRow;

  sortedRows.forEach((item, index) => {
    if (index > 0) {
      ws.duplicateRow(currentRow, 1, true);
      currentRow++;
    }
    const cells = ["A", "B", "C", "D", "E", "F", "G", "H", "I", "J", "K", "L"].map((col) =>
      ws.getCell(`${col}${currentRow}`)
    );
    cells[0].value = finalRoleName;
    cells[1].value = item.territory;
    cells[2].value = item.job_code;
    cells[3].value = item.min ? `£ ${item.min}` : "";
    cells[4].value = item.ntr ? `£ ${item.ntr}` : "";
    cells[5].value = item.exp ? `£ ${item.exp}` : "";
    cells[6].value = item.vexp ? `£ ${item.vexp}` : "";
    cells[7].value = item.max ? `£ ${item.max}` : "";
    cells[8].value = item.core_band_spread ? `£ ${item.core_band_spread}` : "";
    cells[9].value = item.full_range_spread ? `£ ${item.full_range_spread}` : "";
    cells[10].value = item.full_range_ee_count ? `${item.full_range_ee_count}` : "";
    cells[11].value = item.core_band_ee_count ? `${item.core_band_ee_count}` : "";
    cells.forEach(applyBorder);
  });

  const roleEndRow = startRow + sortedRows.length - 1;
  ws.mergeCells(`A${startRow}:A${roleEndRow}`);
  ws.mergeCells(`C${startRow}:C${roleEndRow}`);
  const roleCell = ws.getCell(`A${startRow}`);
  const jobCodeCell = ws.getCell(`C${startRow}`);
  roleCell.value = finalRoleName;
  jobCodeCell.value = sortedRows[0].job_code;
  roleCell.alignment = { vertical: "middle", horizontal: "center" };
  jobCodeCell.alignment = { vertical: "middle", horizontal: "center" };
  for (let row = startRow; row <= roleEndRow; row++) {
    applyBorder(ws.getCell(`A${row}`));
    applyBorder(ws.getCell(`C${row}`));
  }

  try {
    const buffer = await wb.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
    saveAs(blob, "CompensationData.xlsx");
  } catch (err) {
    console.error("Error writing Excel file:", err);
  }
}

// Matches the real .export / .exp-btn markup from the prototype's
// #view-comp export buttons exactly, including data-fmt / data-mod attributes.
// XLS is wired to exportCompensationXls() above. PDF export of the
// compensation view has no equivalent in old Minerva (its "PDF" export on
// this same menu is an unrelated Job Description report) - left disabled
// per go-ahead, not built from scratch.
function ExportControl({ moduleName = "Compensation", jobTitleId, roleName, rows }) {
  return (
    <div className="fb-export export">
      <span className="lbl">Export</span>
      <button
        type="button"
        className="exp-btn exp-btn-disabled"
        data-fmt="PDF"
        data-mod={moduleName}
        disabled
        title="PDF export not available"
      >
        <PdfIcon />
        <span className="fmt">PDF</span>
      </button>
      <button
        type="button"
        className="exp-btn"
        data-fmt="XLS"
        data-mod={moduleName}
        onClick={() => exportCompensationXls(jobTitleId, roleName, rows)}
      >
        <XlsIcon />
        <span className="fmt">XLS</span>
      </button>
    </div>
  );
}
